import { makeAutoObservable } from 'mobx';
import { isValidEmail } from 'hevy-shared';
import { stores } from 'state/stores';
import { fetchUserContentAfterLogin } from 'utils/fetchUserContent';
import { SocialLoginResponse } from 'utils/API';
import { log } from 'utils/log';
import { sendEvent } from 'utils/events';
import axios from 'axios';
import { recaptcha } from './utils';
import { CodeResponse } from '@react-oauth/google';
import router from 'next/router';
import { themeState } from 'styleguide/theme';
import { t } from 'translations/t';

interface SignupViewModelProps {
  coachInviteShortId?: string;
  afterSignup?: (isNewUser: boolean) => Promise<void>;
}

export class SignupViewModel {
  email: string = '';
  password: string = '';
  errorMessage?: string;
  emailErrorStatus?: string;
  passwordErrorStatus?: string;
  gympassUserId?: string;
  isLoading: boolean = false;
  coachInviteShortId?: string;
  afterSignup?: (isNewUser: boolean) => Promise<void>;

  constructor(props?: SignupViewModelProps) {
    this.coachInviteShortId = props?.coachInviteShortId;
    this.afterSignup = props?.afterSignup;
    makeAutoObservable(this);

    log('SignupViewModel constructor');
    sendEvent('signup_screenview');
  }

  /**
   * set error messages and highlight fields with invalid input
   * @param errorMessage error message to display in red text at the bottom of the form
   * @param emailErrorStatus error message to display next to the email field
   * @param passwordErrorStatus error message to display next to the password field
   */
  setError = (
    errorMessage: string | undefined,
    emailErrorStatus?: string,
    passwordErrorStatus?: string,
  ) => {
    this.errorMessage = errorMessage;
    this.emailErrorStatus = emailErrorStatus;
    this.passwordErrorStatus = passwordErrorStatus;
  };

  clearError = () => {
    this.setError(undefined);
  };

  onUpdateEmail = (value: string) => {
    this.email = value;
    this.emailErrorStatus = undefined;
  };

  onUpdatePassword = (value: string) => {
    this.password = value;
    this.passwordErrorStatus = undefined;
  };

  get isValidEmail() {
    return isValidEmail(this.email);
  }

  get isValidPassword() {
    return this.password.length > 5;
  }

  get isSignupButtonEnabled() {
    return this.email.length > 0 && this.password.length > 0;
  }

  onSignup = async () => {
    if (!this.isSignupButtonEnabled) return;

    themeState.push('light');
    try {
      await this.onSignupVerificationPress();
      router.push('/onboarding/verification');
    } catch {
      themeState.pop();
    }
  };

  /**
   * Google Signup
   */

  onSignupWithGoogleSuccess = async (response: CodeResponse) => {
    sendEvent('signup_click', { source: 'google' });

    let signupResult: SocialLoginResponse;

    try {
      signupResult = await stores.auth.signUpWithGoogle(response.code);
    } catch {
      this.onSignupWithGoogleError();
      return;
    }

    sendEvent('signup_complete', { source: 'google' });

    await this.afterSignup?.(signupResult.is_new_user);

    fetchUserContentAfterLogin({ isNewUser: true });

    if (signupResult.is_new_user) {
      router.push('/onboarding/username');
    } else {
      router.push('/');
    }
  };

  onSignupWithGoogleError = () => {
    this.setError(t('web.signup.google.error'));
  };

  /**
   * Apple Signup
   */

  onSignupWithAppleClick = async () => {
    sendEvent('signup_click', { source: 'apple' });
    this.clearError();

    let signupResult: SocialLoginResponse | null = null;

    try {
      const result = await (window as any).AppleID?.auth?.signIn();
      const identityToken = result.authorization?.id_token;
      const maybeEmail = result.user?.email;
      signupResult = await stores.auth.signupWithApple({
        identityToken,
        email: maybeEmail,
        gympassUserId: this.gympassUserId,
      });
    } catch (e) {
      if (axios.isAxiosError(e) && e.response?.data.error === 'popup_closed_by_user') return;

      this.setError(t('web.signup.apple.error'));
      return;
    }

    sendEvent('signup_complete', { source: 'apple' });

    await this.afterSignup?.(signupResult.is_new_user);

    fetchUserContentAfterLogin({ isNewUser: true });

    if (signupResult.is_new_user) {
      router.push('/onboarding/username');
    } else {
      router.push('/');
    }
  };

  /**
   * Email Signup
   */

  onSignupCoachClientPress = async () => {
    if (!this.isValidEmail || !this.isValidPassword) {
      this.setError(
        undefined,
        !this.isValidEmail ? t('web.signup.invalidEmail') : undefined,
        !this.isValidPassword ? t('web.signup.invalidPassword') : undefined,
      );
      throw new Error();
    }

    sendEvent('signup_click', { source: 'email' });
    this.clearError();

    if (!this.coachInviteShortId) {
      await stores.auth.sendSignupVerificationEmail({
        email: this.email,
        password: this.password,
        gympassUserId: this.gympassUserId,
      });
      stores.auth.afterVerificationSuccess = async () => this.afterSignup?.(true);
      router.push('/coach/onboarding/verification');
      return;
    }

    try {
      this.isLoading = true;

      const recaptchaToken = await recaptcha('signup');
      await stores.auth.signupCoachClient({
        email: this.email,
        password: this.password,
        recaptchaToken,
        gympassUserId: this.gympassUserId,
        coachInviteShortId: this.coachInviteShortId,
      });

      sendEvent('signup_complete', { source: 'email' });
      await fetchUserContentAfterLogin({ isCoachOnboardingFlow: true, isNewUser: true });
      await this.afterSignup?.(true);
    } catch (e: unknown | import('axios').AxiosError) {
      this.isLoading = false;

      if (axios.isAxiosError(e)) {
        switch (e.response?.data.error) {
          case 'EmailVerificationNeeded':
            await stores.auth.sendSignupVerificationEmail({
              email: this.email,
              password: this.password,
              gympassUserId: this.gympassUserId,
              coachInviteShortId: this.coachInviteShortId,
            });
            stores.auth.afterVerificationSuccess = async () => this.afterSignup?.(true);
            router.push('/coach/onboarding/verification');
            return;
          case 'EmailAlreadyExists':
            this.setError(t('web.signup.emailBeingUsed'));
            break;
          default:
            sendEvent('signup_error', { source: 'email', error: e.message });
        }

        throw e;
      }

      this.setError(t('global.unknownError'));
      throw e;
    }
  };

  onSignupVerificationPress = async () => {
    if (!this.isValidEmail || !this.isValidPassword) {
      this.setError(
        undefined,
        !this.isValidEmail ? t('web.signup.invalidEmail') : undefined,
        !this.isValidPassword ? t('web.signup.invalidPassword') : undefined,
      );
      throw new Error();
    }

    sendEvent('signup_click', { source: 'email' });
    this.clearError();

    try {
      this.isLoading = true;

      await stores.auth.sendSignupVerificationEmail({
        email: this.email,
        password: this.password,
        gympassUserId: this.gympassUserId,
      });
    } catch (e: unknown | import('axios').AxiosError) {
      this.isLoading = false;

      if (axios.isAxiosError(e)) {
        if (e.response?.status === 409) {
          this.setError(t('web.signup.emailBeingUsed'));
        } else {
          sendEvent('signup_error', { source: 'email', error: e.message });
        }

        throw e;
      }

      this.setError(t('global.unknownError'));
      throw e;
    }
  };
}
